import { useEffect, useMemo, useState } from 'react';

import { useSearchParams } from 'react-router-dom';

import { FormattedMessage, useIntl } from '@zenbusiness/application-commons-intl';
import Heading from '@zenbusiness/zui/Heading';
import { useTheme } from '@zenbusiness/zui/styles';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import { ProductBanners } from '#client/components/ProductBanners';
import { ProductsBreadcrumbs } from '#client/components/ProductsBreadcrumbs';
import { useProductCards, useProductCardsCache } from '#client/hooks/useProductCards';
import useScrollTo from '#client/hooks/useScrollTo';
import { useWebsiteEligibility } from '#client/pages/ProductLanding/hooks/useWebsiteEligibility';
import { DEFAULT_CATEGORY_FILTER, PRODUCTS_FILTER_PARAM_KEY } from '#client/pages/Products/constants';
import { formatSearchParams } from '#client/utils/formatString';

import { BannerConfig } from './bannerConfig';
import { ProductCards } from './components/ProductCards';
import { ProductCardsFilter } from './components/ProductCardsFilter';
import * as Styled from './Products.style';

const getBannerProps = (categoryFilter) => {
  const banner = BannerConfig[categoryFilter];
  if (Array.isArray(banner)) {
    return banner[Math.floor(Math.random() * banner.length)];
  }
  return banner || BannerConfig.all;
};

const Products = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

  const { formatMessage: fm } = useIntl();

  const [searchParams] = useSearchParams();
  const categorySlug = searchParams.get(PRODUCTS_FILTER_PARAM_KEY);
  localStorage.setItem(PRODUCTS_FILTER_PARAM_KEY, categorySlug || DEFAULT_CATEGORY_FILTER);

  const cached = useProductCardsCache(categorySlug);
  const { data: products, loading: queryLoading } = useProductCards(categorySlug);
  const { isSqspEligible, loading: websiteEligibilityLoading } = useWebsiteEligibility();
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let timeout;

    if (cached) {
      setLoading(true);
      timeout = setTimeout(() => {
        setLoading(queryLoading);
      }, 500);
    } else {
      setLoading(queryLoading);
    }

    return () => clearTimeout(timeout);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [queryLoading]);

  const categories = products?.categories || [];

  const productCards = useMemo(() => products?.cards || [], [products?.cards]);
  const categoryActive = categories.find((item) => formatSearchParams(fm({ id: item.titleKey })) === categorySlug);

  // Data for the banner
  const [propsBanner, setPropsBanner] = useState(DEFAULT_CATEGORY_FILTER);

  // Alternate copy/image for sqsp websites.
  const isAltBanner = categorySlug === 'find-customers-online' && !websiteEligibilityLoading && isSqspEligible;

  const resolvedBanner = isAltBanner
    ? {
        ...propsBanner,
        description: propsBanner.descriptionAlt,
        bgrDesktop: propsBanner.bgrDesktopAlt,
        bgrMobile: propsBanner.bgrMobileAlt,
      }
    : propsBanner;

  const isLoading = loading || websiteEligibilityLoading;

  useEffect(() => {
    setPropsBanner(getBannerProps(categorySlug || DEFAULT_CATEGORY_FILTER));
  }, [categorySlug]);

  const bannerImageUrl = isMobile ? resolvedBanner?.bgrMobile : resolvedBanner?.bgrDesktop;

  // Get title page following the category filter
  const titlePage = categoryActive?.titleKey || 'products.title.all';

  // Scroll to top when component mounts
  useScrollTo(0, 0);

  return (
    <Styled.Root>
      <ProductsBreadcrumbs />
      <ProductCardsFilter items={categories} loading={isLoading} />
      <ProductBanners
        imageUrl={bannerImageUrl}
        title={resolvedBanner?.title}
        description={resolvedBanner?.description}
        logo={resolvedBanner?.logo}
        color={resolvedBanner?.color}
        loading={isLoading}
        isAlt={isAltBanner}
      />
      <Heading size="lg" weight="medium" color="primary" loading={isLoading} wrap="wrap">
        <FormattedMessage id={titlePage} />
      </Heading>
      <ProductCards items={productCards} loading={isLoading} />
    </Styled.Root>
  );
};

export default Products;
